import { Image, Tooltip } from 'antd';
import dayjs from 'dayjs';

import { DndContext } from '@dnd-kit/core';

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import React from 'react';

import { MenuOutlined } from '@ant-design/icons';

import { CSS } from '@dnd-kit/utilities';

import { useMutation } from '@apollo/client';

import { ReactComponent as EditIcon } from '@assets/svg/pen-to-square-solid.svg';
import { ReactComponent as DeleteIcon } from '@assets/svg/trash-solid.svg';
import { BREAKPOINTS, DATE_FORMATS } from '@common/constants';
import CommonTable from '@components/CommonTable';
import SearchComponent from '@components/SearchComponent';

import history from '../../../historyData';
import { UPDATE_BLOG_ORDER } from '../graphql/Mutations';

const Row = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && {
        ...transform,
        scaleY: 1,
      },
    ),
    transition,
    ...(isDragging
      ? {
          position: 'relative',
          zIndex: 9999,
        }
      : {}),
  };
  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if (child.key === 'sort') {
          return React.cloneElement(child, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{
                  touchAction: 'none',
                  cursor: 'move',
                }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const BlogsTable = ({
  baseRoute,
  paginationConfig,
  sortConfig,
  handleTableChange,
  blogsData = {},
  loading,
  onSearchChange,
  handleDeleteModalOpen,
  refetchBlogs,
}) => {
  const [updateBlogOrder] = useMutation(UPDATE_BLOG_ORDER, {
    onError: () => {},
    onCompleted: () => {
      refetchBlogs();
    },
  });
  const sortOrder = sortConfig?.sortBy === 'ASC' ? 'ascend' : 'descend';

  const onDragEnd = async ({ active, over }) => {
    if (active.id !== over?.id) {
      const items = blogsData?.data || [];
      const activeIndex = items.findIndex((i) => i?.id === active?.id);
      const overIndex = items.findIndex((i) => i?.id === over?.id);

      if (activeIndex === -1 || overIndex === -1) return;

      const newOrder = overIndex + 1;

      await updateBlogOrder({
        variables: {
          where: { id: active.id },
          data: { newOrder },
        },
      });
    }
  };

  const columns = [
    {
      key: 'sort',
      width: 50,
      // eslint-disable-next-line no-undef
      fixed: window?.innerWidth > BREAKPOINTS?.desktop ? 'left' : false,
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      width: 150,
      sorter: true,
      sortOrder: sortConfig?.sortOn === 'title' ? sortOrder : null,
      align: 'left',
      className: 'max-width-column',
      render: (value) => value ?? '-',
    },
    {
      title: 'SUB TITLE',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      render: (value) => value ?? '-',
    },
    {
      title: 'IMAGE',
      dataIndex: 'image',
      key: 'image',
      width: 80,
      ellipsis: true,
      align: 'center',
      className: 'max-width-column',
      onCell: () => ({
        onClick: (e) => e?.stopPropagation(),
      }),
      render: (value) =>
        value ? <Image src={value} width={30} height={30} /> : '-',
    },
    {
      title: 'LINK',
      dataIndex: 'link',
      key: 'link',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (value) => value ?? '-',
    },
    {
      title: 'ICON',
      dataIndex: 'icon',
      key: 'icon',
      ellipsis: true,
      width: 150,
      align: 'center',
      className: 'max-width-column',
      onCell: () => ({
        onClick: (e) => e?.stopPropagation(),
      }),
      render: (value) =>
        value ? <Image src={value} width={24} height={24} /> : '-',
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      ellipsis: true,
      width: 150,
      sorter: true,
      defaultSortOrder: 'descend',
      sortOrder: sortConfig?.sortOn === 'createdAt' ? sortOrder : null,
      align: 'left',
      className: 'max-width-column',
      render: (value) =>
        value ? dayjs(value)?.format(DATE_FORMATS?.REGULAR) : '-',
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      ellipsis: true,
      width: 150,
      // eslint-disable-next-line no-undef
      fixed: window?.innerWidth > BREAKPOINTS?.desktop ? 'right' : false,
      className: 'cursor-auto',
      onCell: () => ({
        onClick: (e) => e?.stopPropagation(),
      }),
      render: (_, value) => (
        <div className="action-button">
          <Tooltip title="Edit Blog" placement="top" zIndex={4}>
            <EditIcon
              onClick={() => history?.push(`${baseRoute}/edit/${value?.id}`)}
              className="mr-16 pointer svg-icon"
            />
          </Tooltip>
          <Tooltip title="Delete Blog" placement="top" zIndex={4}>
            <DeleteIcon
              onClick={() => handleDeleteModalOpen(value, 'type')}
              className="mr-16 pointer svg-icon"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-end mb-16 flex-wrap">
        <SearchComponent
          onChange={onSearchChange}
          className="search-box ml-16"
        />
      </div>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={(blogsData?.data || []).map((item) => item?.id)}
          strategy={verticalListSortingStrategy}
        >
          <CommonTable
            components={{
              body: {
                row: Row,
              },
            }}
            columns={columns}
            data={blogsData?.data}
            onChange={handleTableChange}
            paginationConfig={{
              total: blogsData?.count,
              current: paginationConfig?.skip,
              pageSize: paginationConfig?.limit,
            }}
            loading={{ spinning: loading, size: 'default' }}
            rowKey={(record) => record?.id}
            rowClassName="pointer"
            onRow={(record) => ({
              onClick: () => history?.push(`${baseRoute}/view/${record?.id}`),
            })}
          />
        </SortableContext>
      </DndContext>
    </>
  );
};

export default BlogsTable;
